import { useState, useCallback, useRef, useEffect } from 'react';

import { searchLibraryVideos, type VideoStockItemResult } from '../../_actions/searchLibraryVideos';
import { SUPPORTED_VIDEO_SEARCH_MEDIA_TYPES, type VideoSearchReturn } from '../types';

const DEBOUNCE_MS = 300;

export function useVideoSearch(): VideoSearchReturn {
    const [showSearch, setShowSearch] = useState(false);
    const [searchTerm, setSearchTermState] = useState('');
    const [results, setResults] = useState<VideoStockItemResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [pageNumber, setPageNumber] = useState(1);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const hasMore = results.length < totalCount;

    const performSearch = useCallback(async (term: string, page: number, append: boolean = false): Promise<void> => {
        // Cancel any pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setError(null);

        try {
            const data = await searchLibraryVideos(term, page);

            // Check if THIS request was aborted (not the current ref, which may be a newer request)
            if (controller.signal.aborted) {
                return;
            }

            if (append) {
                setResults((prev) => [...prev, ...data.stockItems]);
            } else {
                setResults(data.stockItems);
            }
            setTotalCount(data.totalCount);
            setTotalPages(data.totalPages);
            setPageNumber(page);
            setHasSearched(true);
        } catch (err) {
            if (err instanceof Error && err.name === 'AbortError') {
                return;
            }
            setError('Failed to search videos. Please try again.');
            setHasSearched(true);
        } finally {
            if (!controller.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, []);

    const search = useCallback(async (): Promise<void> => {
        await performSearch(searchTerm, 1, false);
    }, [searchTerm, performSearch]);

    const loadMore = useCallback(async (): Promise<void> => {
        if (isLoading || !hasMore) return;
        await performSearch(searchTerm, pageNumber + 1, true);
    }, [isLoading, hasMore, searchTerm, pageNumber, performSearch]);

    const setSearchTerm = useCallback(
        (term: string): void => {
            setSearchTermState(term);

            // Clear any existing debounce timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Debounce the search
            debounceTimerRef.current = setTimeout(() => {
                void performSearch(term, 1, false);
            }, DEBOUNCE_MS);
        },
        [performSearch],
    );

    const goToPage = useCallback(
        async (page: number): Promise<void> => {
            if (page < 1 || page > totalPages || isLoading) return;
            await performSearch(searchTerm, page, false);
        },
        [totalPages, isLoading, searchTerm, performSearch],
    );

    const reset = useCallback((): void => {
        setSearchTermState('');
        setResults([]);
        setTotalCount(0);
        setTotalPages(1);
        setPageNumber(1);
        setError(null);
        setHasSearched(false);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return {
        searchTerm,
        setSearchTerm,
        mediaType: SUPPORTED_VIDEO_SEARCH_MEDIA_TYPES[0],
        results,
        isLoading,
        error,
        hasSearched,
        totalCount,
        hasMore,
        loadMore,
        search,
        reset,
        currentPage: pageNumber,
        totalPages,
        goToPage,
        showSearch,
        setShowSearch,
    };
}
